import { getMetaData } from "../../utils/Constant";
import { useEffect } from "react";

export default function WhenUsePdfAndDoc() {
  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("pdf-docx-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description; // Use .content instead of setAttribute
  }, []);
  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        <div className="row justify-content-center">
          {/* Header */}
          <header
            className="text-center py-5 px-3 position-relative ps-5 pe-5"
            style={{
              color: "#fff",
              background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
            }}
          >
            <h1 className="display-5 fw-bold text-white mb-3 lh-sm">
              PDF vs DOCX: Which Document Format Is Better?
            </h1>
            <div className="text-white mb-4">
              <span className="me-3">• File Format Guide</span>
              <span className="me-3">• Productivity Tips</span>
              <span>• Best Practices</span>
            </div>

            <div className="mt-4 position-relative">
              <span className="badge bg-light text-dark fs-6 me-2 p-2">
                Document Management
              </span>
              <span className="badge bg-light text-dark fs-6 p-2">
                File Formats
              </span>
            </div>
          </header>

          <section className="p-4 p-md-5">
            {/* Executive Summary */}
            <section className="mb-5">
              <div className="card border-start-4 border-start-primary shadow-sm">
                <div className="card-body">
                  <h6 className="card-title text-uppercase fw-bold text-black mb-2">
                    Quick Summary
                  </h6>
                  <p className="card-text mb-0">
                    You've poured hours into crafting your report, resume, or
                    brochure. Now comes a question that’s probably all too
                    familiar:
                    <strong>
                      {" "}
                      “Should I save this as a PDF or a Word (DOCX) file?”
                    </strong>
                    Choose the wrong format, and you might be staring at broken
                    formatting, frustrating collaboration, or compatibility
                    headaches.
                  </p>
                </div>
              </div>
            </section>

            {/* Key Recommendation */}
            <section className="mb-5">
              <div className="alert alert-info border-0 shadow-sm">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h5 className="fw-bold text-dark mb-2">📝 Choose DOCX</h5>
                    <p className="mb-0">
                      When you need to edit, collaborate, or work on drafts
                    </p>
                  </div>
                  <div className="col-md-6">
                    <h5 className="fw-bold text-dark mb-2">📄 Use PDF</h5>
                    <p className="mb-0">
                      For sharing, printing, and final polished versions
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* DOCX Section */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h2 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-primary me-3">1</span>
                    What Is a DOCX File?
                  </h2>
                </div>
                <div className="card-body">
                  <p className="lead mb-4">
                    So, what’s a <strong>DOCX</strong>? It’s the trusty,
                    editable file format from <strong>Microsoft Word</strong> —
                    your flexible playground where you shape your ideas.
                  </p>
                  <p>
                    Need to make changes, add comments, or track edits? DOCX
                    files thrive there. Just a heads up, formatting might wiggle
                    a bit if opened on different software, but that's the
                    trade-off for flexibility.
                  </p>

                  <div className="row g-4 mb-4">
                    <div className="col-md-6">
                      <div className="card h-100 border-primary border-2">
                        <div className="card-body">
                          <h5 className="card-title fw-bold text-primary mb-3">
                            Key Features
                          </h5>
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              ✅ Text, images, and formatting fully editable
                            </li>
                            <li className="mb-2">
                              ✅ Collaboration with comments and track changes
                            </li>
                            <li className="mb-2">
                              ✅ Rich with features: headers, footers, tables,
                              styles
                            </li>
                            <li className="mb-0">
                              ⚠️ Formatting can shift across different platforms
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100 border-success border-2">
                        <div className="card-body">
                          <h5 className="card-title fw-bold text-success mb-3">
                            Best For
                          </h5>
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              • Drafting essays, reports, or any
                              work-in-progress
                            </li>
                            <li className="mb-2">
                              • Team collaboration where changes are constant
                            </li>
                            <li className="mb-2">
                              • Long documents like books or theses needing
                              updates
                            </li>
                            <li className="mb-0">
                              • Gathering and incorporating feedback
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-primary mt-3">
                    <strong>💡 Think of DOCX as your creative workspace</strong>{" "}
                    — where ideas take shape, evolve, and grow.
                  </div>
                </div>
              </div>
            </section>

            {/* PDF Section */}
            <section className="mb-5">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 py-3">
                  <h2 className="h2 fw-bold text-dark mb-0">
                    <span className="badge bg-info me-3">2</span>
                    What Is a PDF File?
                  </h2>
                </div>
                <div className="card-body">
                  <p className="lead mb-4">
                    PDF (Portable Document Format) was created by Adobe to solve
                    a big problem — making sure your document looks just right
                    everywhere.
                  </p>
                  <p>
                    Whether on your laptop, phone, or in a web browser, PDFs
                    keep your formatting locked tight and consistent, making
                    them perfect for final versions.
                  </p>

                  <div className="row g-4 mb-4">
                    <div className="col-md-6">
                      <div className="card h-100 border-info border-2">
                        <div className="card-body">
                          <h5 className="card-title fw-bold text-info mb-3">
                            Key Features
                          </h5>
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              ✅ Looks the same on every device
                            </li>
                            <li className="mb-2">
                              ✅ Viewable easily in browsers without extra
                              software
                            </li>
                            <li className="mb-2">
                              ✅ Great for printing and professional sharing
                            </li>
                            <li className="mb-0">
                              ✅ Offers advanced security and control options
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100 border-warning border-2">
                        <div className="card-body">
                          <h5 className="card-title fw-bold text-warning mb-3">
                            Best For
                          </h5>
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              • Job applications and resumes that need a
                              polished look
                            </li>
                            <li className="mb-2">
                              • Contracts and legal documents where edits should
                              be restricted
                            </li>
                            <li className="mb-2">
                              • Marketing materials like brochures and flyers
                            </li>
                            <li className="mb-0">
                              • eBooks and forms that people print or fill out
                              digitally
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-info mt-3">
                    <strong>💡 Think of PDF as your showcase mode</strong> —
                    polished, professional, and locked in place.
                  </div>
                </div>
              </div>
            </section>

            {/* Comparison Table */}
            <section className="mb-5">
              <h2 className="h2 fw-bold text-dark mb-4 border-bottom pb-2">
                PDF vs DOCX: Detailed Comparison
              </h2>

              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-primary">
                    <tr>
                      <th scope="col" className="fw-bold text-dark">
                        Feature
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        DOCX (Word File)
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        PDF (Portable Document)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Primary Purpose</strong>
                      </td>
                      <td>
                        <span className="badge bg-primary">
                          Editing & Collaboration
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-info">
                          Sharing & Printing
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Formatting Consistency</strong>
                      </td>
                      <td>
                        <span className="badge bg-warning">
                          May shift across devices
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-success">
                          Always consistent
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Editability</strong>
                      </td>
                      <td>
                        <span className="badge bg-success">Fully editable</span>
                      </td>
                      <td>
                        <span className="badge bg-secondary">
                          Needs special tools
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Compatibility</strong>
                      </td>
                      <td>
                        <span className="badge bg-warning">
                          Best with Word/Google Docs
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-success">
                          Opens on any device
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Security Features</strong>
                      </td>
                      <td>
                        <span className="badge bg-warning">
                          Basic password protection
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-success">
                          Advanced encryption
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>File Size</strong>
                      </td>
                      <td>
                        <span className="badge bg-success">
                          Usually smaller
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-warning">
                          Can be larger with images
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Ideal Use Cases</strong>
                      </td>
                      <td>Drafts, reports, editable documents</td>
                      <td>Final versions, resumes, contracts</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Usage Guide */}
            <section className="mb-5">
              <h2 className="h2 fw-bold text-dark mb-4 border-bottom pb-2">
                When to Use Each Format
              </h2>

              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card h-100 border-primary border-2">
                    <div className="card-header bg-primary text-white">
                      <h3 className="h4 mb-0">📝 Use DOCX When...</h3>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled">
                        <li className="mb-3">
                          <strong>🔄 Collaborative Editing</strong>
                          <br />
                          <small className="text-muted">
                            Working with teammates who need to make changes
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>📝 Draft Documents</strong>
                          <br />
                          <small className="text-muted">
                            Essays, reports, or documents still in progress
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>📊 Long Documents</strong>
                          <br />
                          <small className="text-muted">
                            Books, theses, or business plans requiring updates
                          </small>
                        </li>
                        <li className="mb-0">
                          <strong>💬 Feedback Collection</strong>
                          <br />
                          <small className="text-muted">
                            When you need comments and suggestions
                          </small>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card h-100 border-info border-2">
                    <div className="card-header bg-info text-white">
                      <h3 className="h4 mb-0">📄 Use PDF When...</h3>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled">
                        <li className="mb-3">
                          <strong>🎯 Professional Sharing</strong>
                          <br />
                          <small className="text-muted">
                            Job applications, resumes, official documents
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>🖨️ Printing</strong>
                          <br />
                          <small className="text-muted">
                            Brochures, flyers, or any print materials
                          </small>
                        </li>
                        <li className="mb-3">
                          <strong>🔒 Security</strong>
                          <br />
                          <small className="text-muted">
                            Contracts or sensitive information
                          </small>
                        </li>
                        <li className="mb-0">
                          <strong>📱 Universal Access</strong>
                          <br />
                          <small className="text-muted">
                            When recipients may not have Word
                          </small>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Conversion Guide */}
            <section className="mb-5">
              <h2 className="h2 fw-bold text-dark mb-4 border-bottom pb-2">
                Converting Between Formats
              </h2>

              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card h-100 border-success border-2">
                    <div className="card-body text-center">
                      <h4 className="card-title text-success mb-3">
                        DOCX → PDF
                      </h4>
                      <div
                        className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: "60px", height: "60px" }}
                      >
                        <span className="fw-bold text-success">✓</span>
                      </div>
                      <p className="card-text">
                        Simple and reliable. Just use <em>Save As PDF</em> or{" "}
                        <em>Export</em> from your editor. Your layout stays
                        exactly as you designed it.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card h-100 border-warning border-2">
                    <div className="card-body text-center">
                      <h4 className="card-title text-warning mb-3">
                        PDF → DOCX
                      </h4>
                      <div
                        className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: "60px", height: "60px" }}
                      >
                        <span className="fw-bold text-warning">⚠️</span>
                      </div>
                      <p className="card-text">
                        Possible, but it’s a bit tricky. Complex tables and
                        graphics might not convert smoothly. If you need heavy
                        edits, ask for the original DOCX if possible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Quick Decision Guide */}
            <section className="mb-5">
              <h2 className="h2 fw-bold text-dark mb-4 border-bottom pb-2">
                Quick Decision Guide
              </h2>

              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="table-light">
                    <tr>
                      <th scope="col" className="fw-bold text-dark">
                        Your Goal
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        Recommended Format
                      </th>
                      <th scope="col" className="fw-bold text-dark">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Edit, update, or collaborate</td>
                      <td>
                        <span className="badge bg-primary">DOCX</span>
                      </td>
                      <td>Built for editing and teamwork</td>
                    </tr>
                    <tr>
                      <td>Share, print, or submit final version</td>
                      <td>
                        <span className="badge bg-info">PDF</span>
                      </td>
                      <td>Consistent appearance everywhere</td>
                    </tr>
                    <tr>
                      <td>Ensure consistent design</td>
                      <td>
                        <span className="badge bg-info">PDF</span>
                      </td>
                      <td>Formatting locked in place</td>
                    </tr>
                    <tr>
                      <td>Keep it easily editable</td>
                      <td>
                        <span className="badge bg-primary">DOCX</span>
                      </td>
                      <td>Native editing capabilities</td>
                    </tr>
                    <tr>
                      <td>Add security or restrict editing</td>
                      <td>
                        <span className="badge bg-info">PDF</span>
                      </td>
                      <td>Advanced security features</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-5">
              <div
                className="card border-0 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                }}
              >
                <div className="card-body p-4">
                  <h3 className="h4 fw-bold mb-3">Final Recommendation</h3>
                  <p className="mb-4 opacity-90">
                    When it comes down to it, there’s no single “winner” between
                    PDF and DOCX — it’s all about using the right tool for your
                    specific job. Nail that choice, and your documents will look
                    professional, be easy to handle, and save you plenty of
                    headaches.
                  </p>
                  <div className="row text-center">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <div className="bg-white bg-opacity-20 text-black rounded p-3">
                        <strong className="d-block h5 mb-1">DOCX</strong>
                        <small className="opacity-90">Create & Edit</small>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="bg-white text-black bg-opacity-20 rounded p-3">
                        <strong className="d-block h5 mb-1">PDF</strong>
                        <small className="opacity-90">Share & Preserve</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="alert alert-warning mt-4">
                <p className="mb-0">
                  <strong>💬 Share Your Experience:</strong> Ever had a
                  formatting disaster because of the wrong file format? We’d
                  love to hear your story — and maybe save someone else from the
                  same headache!
                </p>
              </div>
            </section>

            {/* Resources */}
            <footer className="border-top pt-4">
              <h6 className="fw-bold text-dark mb-3">Related Topics & Tags</h6>
              <div className="d-flex flex-wrap gap-2">
                <span className="badge bg-secondary">#PDFvsDOCX</span>
                <span className="badge bg-secondary">#FileFormats</span>
                <span className="badge bg-secondary">#DocumentManagement</span>
                <span className="badge bg-secondary">#ProductivityTips</span>
                <span className="badge bg-secondary">#MicrosoftWord</span>
                <span className="badge bg-secondary">#AdobeAcrobat</span>
                <span className="badge bg-secondary">#OfficeTips</span>
                <span className="badge bg-secondary">#DocumentSharing</span>
              </div>
            </footer>
          </section>
        </div>
      </article>
    </div>
  );
}
